import React, { useState, useEffect } from 'react';
import { 
  createPaymentOrder, 
  verifyPayment,
  recoverPayment,
  getRazorpayKeyId 
} from '../api/api';
import './PaymentModal.css';

function PaymentModal({ 
  isOpen, 
  onClose, 
  campaignId, 
  creators, 
  backupCreators = [],
  onPaymentSuccess,
  brandEmail,
  brandName 
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [escrowAmount, setEscrowAmount] = useState(0);
  const [platformFee, setPlatformFee] = useState(0);
  const [topNCreators, setTopNCreators] = useState([]);
  const [creatorAllocations, setCreatorAllocations] = useState([]);
  const PLATFORM_FEE_PERCENT = 3.5;

  useEffect(() => {
    if (creators && creators.length > 0) {
      // Calculate escrow based on Top N most expensive creators (N = primary count)
      const requiredCreators = creators.length;
      
      // Create allocations for API call
      const allocations = creators.map(creator => ({
        creator_id: creator.id,
        creator_name: creator.instagram_username || creator.name || 'Creator',
        recommended_price: creator.pricing?.suggested_price || 0,
        adjusted_price: creator.adjusted_price || creator.pricing?.suggested_price || 0,
        content_type: creator.content_type || 'reel'
      }));
      setCreatorAllocations(allocations);
      
      // Combine primary and backup with their prices
      const allCreators = [
        ...creators.map(c => ({
          id: c.id,
          name: c.instagram_username || c.name || 'Creator',
          price: c.adjusted_price || c.pricing?.suggested_price || 0,
          type: 'primary'
        })),
        ...(backupCreators || []).map(c => ({
          id: c.id,
          name: c.instagram_username || c.name || 'Backup Creator',
          price: c.adjusted_price || c.pricing?.suggested_price || 0,
          type: 'backup'
        }))
      ];
      
      // Sort by price descending and take top N
      const sorted = [...allCreators].sort((a, b) => b.price - a.price);
      const topN = sorted.slice(0, requiredCreators);
      setTopNCreators(topN);
      
      // Calculate escrow amount (Top N + platform fee)
      const topNTotal = topN.reduce((sum, c) => sum + c.price, 0);
      const fee = topNTotal * (PLATFORM_FEE_PERCENT / 100);
      
      setEscrowAmount(topNTotal);
      setPlatformFee(fee);
    }
  }, [creators, backupCreators]);

  const handlePayment = async () => {
    setLoading(true);
    setError('');

    const totalToPay = escrowAmount + platformFee;

    try {
      // Create payment order with escrow amount
      const orderResponse = await createPaymentOrder({
        campaign_id: campaignId,
        creator_allocations: creatorAllocations.map(a => ({
          creator_id: a.creator_id,
          recommended_price: a.recommended_price,
          adjusted_price: a.adjusted_price,
          content_type: a.content_type
        })),
        escrow_amount: escrowAmount,
        platform_fee: platformFee,
        top_n_creators: topNCreators
      });

      const orderData = orderResponse.data;

      // Initialize Razorpay
      const options = {
        key: getRazorpayKeyId(),
        amount: orderData.amount_paise,
        currency: 'INR',
        name: 'Milan AI',
        description: `Campaign Escrow - ${creators.length} Creator(s)`,
        order_id: orderData.razorpay_order_id,
        handler: async function (response) {
          try {
            // Verify payment
            await verifyPayment({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              campaign_id: campaignId
            });

            setLoading(false);
            onPaymentSuccess && onPaymentSuccess({
              amount: totalToPay,
              escrow_amount: escrowAmount,
              platform_fee: platformFee,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_order_id: response.razorpay_order_id
            });
          } catch (verifyError) {
            // Verify can fail on a flaky network even after Razorpay captured funds.
            // Poll Razorpay once via our recover endpoint (same as dashboard draft scan).
            try {
              const recRes = await recoverPayment(campaignId);
              const d = recRes?.data;
              if (d?.ready_to_finalize) {
                const paidAmount =
                  typeof d.amount === 'number' ? d.amount : totalToPay;
                setLoading(false);
                onPaymentSuccess && onPaymentSuccess({
                  amount: paidAmount,
                  escrow_amount: escrowAmount,
                  platform_fee: platformFee,
                  razorpay_payment_id:
                    d.razorpay_payment_id || response.razorpay_payment_id,
                  razorpay_order_id:
                    d.razorpay_order_id || response.razorpay_order_id,
                });
                return;
              }
            } catch (_) {
              /* fall through to message below */
            }
            setError(
              'Could not confirm payment. If your bank charged you, refresh this page or open your dashboard.'
            );
            setLoading(false);
          }
        },
        prefill: {
          email: brandEmail || '',
          name: brandName || ''
        },
        theme: {
          color: '#8b5cf6'
        },
        modal: {
          ondismiss: function() {
            setLoading(false);
          }
        }
      };

      // Check if Razorpay SDK is loaded
      if (!window.Razorpay) {
        throw new Error('Payment gateway not loaded. Please refresh the page and try again.');
      }
      
      const razorpay = new window.Razorpay(options);
      razorpay.open();
      
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to create payment order');
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="payment-modal-overlay" onClick={onClose}>
      <div className="payment-modal" onClick={(e) => e.stopPropagation()}>
        <div className="payment-modal-header">
          <h2>Campaign Payment</h2>
          <button className="close-btn" onClick={onClose}>&times;</button>
        </div>

        <div className="payment-modal-body">
          {error && <div className="payment-error">{error}</div>}

          <div className="payment-info-banner">
            <span className="info-icon">ℹ️</span>
            <p>
              Funds will be locked in escrow until campaign completion. Fixed payments (60%) are 
              released weekly during monitoring, and performance (40%) is released after monitoring completes.
            </p>
          </div>

          {/* Smart Escrow Breakdown */}
          {topNCreators.length > 0 && (
            <div className="escrow-breakdown">
              <h3>🔒 Smart Escrow Calculation</h3>
              <p className="escrow-explanation">
                We block based on the <strong>top {creators.length} most expensive creators</strong> from your selection to ensure campaign can be fulfilled.
              </p>
              <div className="top-n-list">
                {topNCreators.map((creator, idx) => (
                  <div key={creator.id} className={`top-n-item ${creator.type}`}>
                    <span className="rank">#{idx + 1}</span>
                    <span className="name">{creator.name}</span>
                    <span className={`type-badge ${creator.type}`}>
                      {creator.type === 'primary' ? '🎯 Primary' : '🔄 Backup'}
                    </span>
                    <span className="price">₹{creator.price.toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="payment-summary">
            <h3>Payment Summary</h3>
            <div className="summary-row">
              <span>Escrow Amount (Top {creators.length} Creators)</span>
              <span>₹{escrowAmount.toLocaleString()}</span>
            </div>
            <div className="summary-row fee">
              <span>Platform Fee ({PLATFORM_FEE_PERCENT}%)</span>
              <span>₹{Math.round(platformFee).toLocaleString()}</span>
            </div>
            <div className="summary-row total">
              <span>Total Amount to Block</span>
              <span>₹{Math.round(escrowAmount + platformFee).toLocaleString()}</span>
            </div>
          </div>

          <div className="creator-payout-note">
            <strong>How it works:</strong> Amount is blocked in escrow. When creators accept, actual price is charged. 
            If cheaper creators accept, difference is refunded. Creators receive 60% fixed weekly + 40% performance after monitoring.
          </div>

          {/* Important notice about what happens after payment */}
          <div className="payment-warning-banner">
            <span className="warning-icon">⚠️</span>
            <div>
              <strong>After payment, invitations will be sent automatically</strong>
              <p>Email invitations will be sent to all {creators?.length || 0} selected creators. This action cannot be undone.</p>
            </div>
          </div>
        </div>

        <div className="payment-modal-footer">
          <button className="cancel-btn" onClick={onClose} disabled={loading}>
            Cancel
          </button>
          <button 
            className="pay-btn" 
            onClick={handlePayment} 
            disabled={loading || escrowAmount === 0}
          >
            {loading ? 'Processing...' : `💳 Pay ₹${Math.round(escrowAmount + platformFee).toLocaleString()} & Send Invitations`}
          </button>
        </div>
      </div>
    </div>
  );
}

export default PaymentModal;
